import React, { useEffect, useRef, useState } from "react";
import { isNotEmpty, useForm } from "@mantine/form";
import { Alert, Button, Grid, GridCol, Loader } from "@mantine/core";
import ModalButtons from "components/ui/Modal/ModalButtons";
import defaults from "config/forms.json";
import { MSG } from "constants/msg";
import {
  DntInfo,
  FormFileFieldLabels,
  FormFileProps,
  FormFileValues,
} from "types/module/Firmware";
import { useFirmwareDntSubmit, useFirmwareDntUpdate } from "hooks/useFirmware";
import { isVersion } from "utils/FormValidation";
import VersionComparison from "./VersionComparison";
import FormInitial from "./FormInitial";
import FormReadOnly from "./FormReadOnly";
import { FaExclamationCircle } from "react-icons/fa";

const DNT_INFO: DntInfo = {
  dntFirmwareFile: "",
  sha256: "",
  product: "",
  firmwareVersion: "",
  modelName: "",
  modelId: "",
  productVersion: "",
  messageDetails: "",
  messageComparison: {},
};

const FIELD_LABELS: FormFileFieldLabels = {
  manufacturer: "Manufacturer",
  releaseNotesUrl: "Release Notes URL",
  comments: "Comments",
  firmwareFile: "Firmware File",
  danteProductFamily: "Dante Product Family",
  danteFirmware: "Dante Firmware Version",
  modelName: "Product Model Name",
  modelVersion: "Product Version",
  modelId: "Model ID",
  agree: "I agree",
  deviceCode: "Dante Product Family",
};

const FormFile: React.FC<FormFileProps> = ({
  firmwareDataRefetch,
  onClose,
  uploadProgress,
  setUploadProgress,
  isNonDntSubmitting,
  setIsNonDntSubmitting,
  firmwareNonDntSubmitMutate,
  isFirmwareNonDntSubmitSuccess,
  firmwareDntInitiateSubmitData,
  firmwareDntInitiateSubmitMutate,
  isFirmwareDntInitiateSubmitSuccess,
  isFirmwareDntInitiateSubmitError,
}) => {
  const form = useForm<FormFileValues>({
    initialValues: {
      releaseNotesUrl: "",
      comments: "",
      firmwareFile: null,
      agree: false,
      deviceCode: "",
      danteFirmware: "",
      modelName: "",
      modelId: "",
      modelVersion: "",
    },
    validate: (values) => {
      const errors: Record<string, string | null> = {
        releaseNotesUrl:
          values.releaseNotesUrl.length > defaults.input.maxLength.default
            ? `${FIELD_LABELS.releaseNotesUrl} cannot exceed ${defaults.input.maxLength.default} characters`
            : null,

        comments:
          values.comments.length > defaults.textArea.maxLength.default
            ? `${FIELD_LABELS.comments} cannot exceed ${defaults.textArea.maxLength.default} characters`
            : null,

        firmwareFile: (() => {
          const value = values.firmwareFile;
          if (!value) return MSG.VALIDATE.REQUIRED(FIELD_LABELS.firmwareFile);

          const fileLimit = isNotDnt
            ? defaults.file.maxSize.nonDnt
            : defaults.file.maxSize.dnt;

          const maxSize = fileLimit * 1024 * 1024;

          if (value.size > maxSize) {
            return `${FIELD_LABELS.firmwareFile} size cannot exceed ${fileLimit}MB`;
          }
          return null;
        })(),

        agree:
          formsReadOnly && !values.agree
            ? MSG.VALIDATE.REQUIRED(FIELD_LABELS.agree)
            : null,
      };

      if (formsReadOnly && isNotDnt) {
        errors.deviceCode = isNotEmpty(
          MSG.VALIDATE.REQUIRED(FIELD_LABELS.deviceCode)
        )(values.deviceCode) as string | null;

        errors.danteFirmware = isNotEmpty(
          MSG.VALIDATE.REQUIRED(FIELD_LABELS.danteFirmware)
        )(values.danteFirmware) as string | null;

        errors.modelName = (() => {
          const value = values.modelName;
          if (!value) return MSG.VALIDATE.REQUIRED(FIELD_LABELS.modelName);

          if (values.modelName.length > defaults.input.maxLength.modelName) {
            return `${FIELD_LABELS.modelName} cannot exceed ${defaults.input.maxLength.modelName} characters`;
          }
          return null;
        })();

        errors.modelId = isNotEmpty(
          MSG.VALIDATE.REQUIRED(FIELD_LABELS.modelId)
        )(values.modelId) as string | null;

        errors.modelVersion = (() => {
          const value = values.modelVersion;
          const isVersionResult = isVersion(value, "Numeric");

          if (!value?.trim())
            return MSG.VALIDATE.REQUIRED(FIELD_LABELS.modelVersion);
          if (isVersionResult !== true) return isVersionResult;
          return null;
        })();
      }

      return errors;
    },
  });

  const [fileError, setFileError] = useState<string>("");
  const [formsReadOnly, setFormsReadOnly] = useState<boolean>(false);
  const [dntInfo, setDntInfo] = useState<DntInfo>(DNT_INFO);
  const [isNotDnt, setIsNotDnt] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  const hasInitiatedChunkedSubmitRef = useRef(false);

  const {
    mutate: firmwareSubmitMutate,
    data: firmwareSubmitData,
    isSuccess: isFirmwareSubmitSuccess,
    isError: isFirmwareSubmitError,
  } = useFirmwareDntSubmit(setUploadProgress);

  const {
    mutate: firmwareUpdateMutate,
    isSuccess: isFirmwareUpdateSuccess,
    isError: isFirmwareUpdateError,
  } = useFirmwareDntUpdate();

  useEffect(() => {
    const isFreshDntInitiateSuccess =
      isFirmwareDntInitiateSubmitSuccess &&
      hasInitiatedChunkedSubmitRef.current;

    if (isFirmwareSubmitSuccess || isFreshDntInitiateSuccess) {
      const firmwareData = firmwareSubmitData?.data || firmwareDntInitiateSubmitData?.data?.data;
      if (firmwareData?.result === "failed") {
        setFileError(firmwareData?.errorMessage);
        setFormsReadOnly(false);
        setUploadProgress(0);
      } else {
        setFileError("");
        setFormsReadOnly(true);
        
        let messageComparison = null;

        if (firmwareData.dntInfo?.dntCapabilities.length > 0) {
          if (firmwareData.previousDntCapabilities && firmwareData.previousDntCapabilities.length !== 0) {
            const previousDntCapabilities = firmwareData.previousDntCapabilities;
            messageComparison =
              previousDntCapabilities.pending.existingDnt &&
              previousDntCapabilities.pending.existingDnt?.length > 0
                ? previousDntCapabilities.pending.existingDnt[0].dntCapabilities[0]
                : previousDntCapabilities.published.existingDnt &&
                  previousDntCapabilities.published.existingDnt.length > 0
                ? previousDntCapabilities.published.existingDnt[0].dntCapabilities[0]
                : null;
          }

          setDntInfo({
            dntFirmwareFile: firmwareData.fileName,
            sha256: firmwareData.dntInfo.sha256,
            id: firmwareData.id,
            product: firmwareData.dntInfo.dntCapabilities[0].danteProduct,
            firmwareVersion: firmwareData.dntInfo.dntCapabilities[0].danteFirmwareVersion,
            modelName: firmwareData.dntInfo.dntCapabilities[0].oemModelName,
            modelId: firmwareData.dntInfo.dntCapabilities[0].modelId,
            productVersion: firmwareData.dntInfo.dntCapabilities[0].oemModelVersion,
            messageDetails: firmwareData.messageDetails,
            messageComparison,
          });
        } 
      }
    }

    if (isFirmwareSubmitSuccess || isFirmwareSubmitError
      || isFirmwareDntInitiateSubmitSuccess || isFirmwareDntInitiateSubmitError
    ) {
      setIsSubmitting(false);
    }
  }, [isFirmwareSubmitSuccess, isFirmwareSubmitError,
    isFirmwareDntInitiateSubmitSuccess, isFirmwareDntInitiateSubmitError
  ]);

  useEffect(() => {
    if (isFirmwareUpdateSuccess || isFirmwareNonDntSubmitSuccess) {
      handleResetForm();
      firmwareDataRefetch();

      if (isFirmwareUpdateSuccess) onClose();
    }

    if (isFirmwareUpdateSuccess || isFirmwareUpdateError) {
      setIsSubmitting(false);
    }
  }, [
    isFirmwareUpdateSuccess,
    isFirmwareUpdateError,
    isFirmwareNonDntSubmitSuccess,
  ]);

  const handleResetForm = () => {
    setUploadProgress(0);
    setFormsReadOnly(false);
    setFileError("");
    setDntInfo(DNT_INFO);
    setIsNotDnt(false);
    hasInitiatedChunkedSubmitRef.current = false;

    form.reset();
  };

  const handleFileSubmit = (data: FormFileValues) => {
    if (!formsReadOnly) {
      // STEP 1
      if (!isNotDnt) {
        setIsSubmitting(true);
        // DNT
        if (data.firmwareFile.size > defaults.file.maxSize.chunk * 1024 * 1024) {
            // DNT - Chunked Upload
            hasInitiatedChunkedSubmitRef.current = true;
            firmwareDntInitiateSubmitMutate({
              file: data.firmwareFile,
            });
        } else {
            // DNT - Single Upload
            firmwareSubmitMutate({
              file: data.firmwareFile,
            });
        }
      } else {
        // NON DNT
        setFileError("");
        setFormsReadOnly(true);
        setDntInfo((prev) => ({
          ...prev,
          dntFirmwareFile: data.firmwareFile?.name || "",
        }));
      }
    } else {
      // STEP 2
      if (!isNotDnt) {
        // DNT
        setIsSubmitting(true);
        firmwareUpdateMutate({
          sha256: dntInfo.sha256,
          status: "submit",
          releaseNotesUrl: data.releaseNotesUrl,
          comments: data.comments,
          fileName: dntInfo.dntFirmwareFile,
          fileId: dntInfo.id,
          modelId: dntInfo.modelId,
          modelName: dntInfo.modelName,
          modelVersion: dntInfo.productVersion,
          product: dntInfo.product,
        });
      } else {
        // NON DNT
        setIsNonDntSubmitting(true);
        firmwareNonDntSubmitMutate({
          file: data.firmwareFile,
          deviceCode: JSON.parse(data.deviceCode)[0],
          danteFirmware: data.danteFirmware,
          modelName: data.modelName,
          modelId: data.modelId,
          modelVersion: data.modelVersion,
          product: JSON.parse(data.deviceCode)[1],
          releaseNotesUrl: data.releaseNotesUrl,
          comments: data.comments,
        });
      }
    }
  };

  return (
    <form
      onSubmit={form.onSubmit((values: FormFileValues) =>
        handleFileSubmit(values)
      )}
    >
      <Grid>
        {!isNonDntSubmitting ? (
          <>
            {!formsReadOnly && (
              <GridCol span={12}>
                Please submit a separate form for firmware files corresponding to
                each product family that you need to upload.
              </GridCol>
            )}

            {formsReadOnly &&
              !isNotDnt &&
              dntInfo.messageDetails &&
              dntInfo.messageDetails !== "" &&
              dntInfo.messageComparison && (
                <VersionComparison
                  fieldLabels={FIELD_LABELS}
                  dntInfo={dntInfo}
                />
              )}
            <FormInitial
              fieldLabels={FIELD_LABELS}
              form={form}
              formsReadOnly={formsReadOnly}
              isSubmitting={isSubmitting || isNonDntSubmitting}
              setIsNotDnt={setIsNotDnt}
              fileError={fileError}
            />
            {formsReadOnly && (
              <FormReadOnly
                fieldLabels={FIELD_LABELS}
                form={form}
                isNotDnt={isNotDnt}
                dntInfo={dntInfo}
              />
            )}
          </>
        ) : (
          <Alert color="yellow" icon={<FaExclamationCircle />}>
            Firmware file upload is still in progress. Please do not close or
            refresh the browser until the upload is complete.
          </Alert>
        )}
      </Grid>
      <ModalButtons
        start={
          <Button
            onClick={handleResetForm}
            disabled={isSubmitting || isNonDntSubmitting}
            color="orange"
          >
            {!formsReadOnly ? "Reset Form" : "Cancel and Upload Another"}
          </Button>
        }
      >
        <Button type="submit" disabled={isSubmitting || isNonDntSubmitting}>
          {isSubmitting || isNonDntSubmitting ? (
            <>
              <Loader size={15} color="gray" mr="sm" />
              {uploadProgress !== 100
                ? `Uploading (${uploadProgress}%)`
                : `Submitting...`}
            </>
          ) : (
            <>
              {!formsReadOnly && "Submit Firmware File"}
              {formsReadOnly && "Confirm and Finalize Submission"}
            </>
          )}
        </Button>
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </ModalButtons>
    </form>
  );
};

export default FormFile;
